import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../../hooks/useDebounceValue';
import {
	ADMIN_USER_ROLE_LABELS,
	ADMIN_USER_ROLE_OPTIONS,
	DISPLAY_TO_API_STATUS,
	useUserFilters,
} from '../../../../../hooks/useUserFilters';
import { useAdminUserSession } from '../../../../../models/hooks/adminStateHooks';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { getDisplayName } from '../../../../../models/NameUtils';
import { copyToClipboard, isSupportedISP } from '../../../../../models/UiUtils';
import {
	invalidateAdminAccountUsersQuery,
	useAdminAccountUsersQuery,
} from '../../../../../queries/AdminTools/useAdminAccountUsersQuery';
import { useResendWelcomeEmailMutation } from '../../../../../queries/AdminTools/useResendWelcomeEmailMutation';
import { useExportCommunicationHistoryMutation } from '../../../../../queries/User/useExportCommunicationHistoryMutation';
import { AccountUserViewModel, AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { DeactivateUserModal } from '../../../../../web/components/DeactivateUserModal';
import { ExportConfirmationModalV2 } from '../../../../../web/components/ExportConfirmation';
import { DangerMessage } from '../../../../../web/components/errorMessages/DangerMessage';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../../web/components/Modal';
import { InviteMoreUsersIcon } from '../../../../../web/components/svgs/icons/InviteMoreUsersIcon';
import { RefreshIcon } from '../../../../../web/components/svgs/icons/RefreshIcon';
import { UserFilters } from '../../../../../web/components/UserFilters';
import { Button } from '../../../../../web/components-v2/Button';
import { brandPrimary, brandPrimaryHover } from '../../../../../web/styles/colors';
import { AccountUserRow } from '../../../../components/accountDetails/AccountUserRow';
import { GrantSlotMachineSpinModal } from '../../../../components/accountDetails/GrantSlotMachineSpinModal';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { ContactImport } from '../../../../components/ContactImport';
import { ImportHistory } from '../../../../components/ImportHistory';
import { EmailChangeModal, EmailChangeType } from '../../../../components/tools/EmailChangeModal';
import { SharedMailboxModal } from '../../../../components/tools/SharedMailboxModal';
import { UserDialogContent } from '../../../../components/user/UserDialogContent';
import { UserErrorsDialog } from '../../../../components/user/UserErrors';
import { isResellerSession } from '../../../../contexts/ResellerContext';
import { styleSheet } from './styles';

interface Props {
	account: Api.IAccount;
	grantTemporaryAccess?(): Promise<void>;
	onMakeMainContact?(contactId: string): void;
	scrollToBottomPortalId?: string;
}

const PAGE_SIZE = 50;

function _Users({
	account,
	grantTemporaryAccess,
	onMakeMainContact: onMakeMainContactProp,
	scrollToBottomPortalId: _,
}: Props) {
	const userSession = useUserSession();
	const adminUserSession = useAdminUserSession();
	const isReseller = isResellerSession(adminUserSession);
	const { logApiError } = useEventLogging('Users');
	const errorMessages = useErrorMessages();
	const [page, setPage] = React.useState(0);
	const queryClient = useQueryClient();
	const [selectedUser, setSelectedUser] = React.useState<AccountUserViewModel | null>(null);
	const [showImport, setShowImport] = React.useState(false);
	const [userForErrorsDialog, setUserForErrorsDialog] = React.useState<AccountUserViewModel | null>(null);
	const [userForImportHistoryDialog, setUserForImportHistoryDialog] = React.useState<AccountUserViewModel | null>(null);
	const [userForInfoDialog, setUserForInfoDialog] = React.useState<AccountUserViewModel | null>(null);
	const [userForEmailChangeModal, setUserForEmailChangeModal] = React.useState<AccountUserViewModel | null>(null);
	const [userForAuthenticatorChangeEmailModal, setUserForAuthenticatorChangeEmailModal] =
		React.useState<AccountUserViewModel | null>(null);
	const [userForSharedMailboxModal, setUserForSharedMailboxModal] = React.useState<AccountUserViewModel | null>(null);
	const [loadingId, setLoadingId] = React.useState('');
	const [showingSlotMachineSpinModal, setShowingSlotMachineSpinModal] = React.useState(false);
	const [showDeactivateConfirmation, setShowDeactivateConfirmation] = React.useState(false);
	const [userForDeactivateConfirmDialog, setUserForDeactivateConfirmDialog] =
		React.useState<AccountUserViewModel | null>(null);

	// getUserRole/getDisplayName are required by useUserFilters but unused here — filtering is server-side
	const userFilters = useUserFilters<AccountUserViewModel>({
		roleOptions: ADMIN_USER_ROLE_OPTIONS,
		roleLabels: ADMIN_USER_ROLE_LABELS,
		getUserRole: (user: AccountUserViewModel) => user.role,
		getDisplayName: (user: AccountUserViewModel) => user.name,
		onFilterChange: () => setPage(0),
	});

	const { statuses, roles, searchText } = userFilters.filters;
	const debouncedSearchText = useDebounceValue(searchText, 300);

	// Translate display status labels to API values (CREATED → INIT)
	const apiStatuses = statuses.map(s => DISPLAY_TO_API_STATUS[s] ?? s);

	const {
		data: usersPage,
		isPending,
		isFetching,
		isError,
		error,
		refetch,
	} = useAdminAccountUsersQuery({
		accountId: account?.id ?? '',
		page,
		pageSize: PAGE_SIZE,
		statuses: apiStatuses,
		roles,
		search: debouncedSearchText,
	});

	const userViewModels = React.useMemo(
		() => (usersPage?.values ?? []).map(u => new AccountUserViewModel(userSession, u)),
		[usersPage?.values, userSession]
	);

	const exportCommunicationHistoryMutation = useExportCommunicationHistoryMutation();

	const onExportCommunicationHistoryClosed = () => {
		exportCommunicationHistoryMutation.reset();
	};

	const importClick = (user: AccountUserViewModel) => () => {
		setSelectedUser(user);
		setShowImport(true);
	};
	const onInviteLinkClicked = () => {
		if (account) {
			const domainQueryString =
				!!account.emailDomain && account.emailDomain !== 'none.com' && account.emailDomain !== '@none.com'
					? '&domain=' + encodeURIComponent(account.emailDomain)
					: '';
			window.open(
				`${import.meta.env.VITE_APP_URL}/#/user/register/?a=` +
					account.id +
					'&invitedBy=' +
					userSession.user.firstName +
					domainQueryString
			);
		}
	};
	const onChangeEmail = (user: AccountUserViewModel) => () => {
		setUserForEmailChangeModal(user);
	};
	const onAuthenticatorChangeEmail = (user: AccountUserViewModel) => () => {
		setUserForAuthenticatorChangeEmailModal(user);
	};
	const onSharedMailbox = (user: AccountUserViewModel) => () => {
		setUserForSharedMailboxModal(user);
	};
	const onCreateWelcomeEmailClick = (user: AccountUserViewModel) => async () => {
		try {
			const link = await user.getWelcomeEmailLink();
			const done = copyToClipboard(link.url);
			if (done) {
				toast.success('Copied account link to clipboard!');
			} else {
				toast.error('Unable to copy to clipboard.');
			}
		} catch (err) {
			logApiError('CreateWelcomeEmail-Error', err);
			errorMessages.pushApiError(err);
		}
	};
	const onGrantSlotMachineSpinClicked = (user: AccountUserViewModel) => {
		setSelectedUser(user);
		setShowingSlotMachineSpinModal(true);
	};
	const onImportHistory = (user: AccountUserViewModel) => () => {
		setUserForImportHistoryDialog(user);
	};
	const onMakeMainContact = (user: AccountUserViewModel) => () => {
		if (onMakeMainContactProp) {
			onMakeMainContactProp(user.id);
		}
	};
	const onUserDeleted = () => {
		invalidateAdminAccountUsersQuery(queryClient, account?.id ?? '');
	};
	const onUserErrorsClick = (user: AccountUserViewModel) => () => {
		setUserForErrorsDialog(user);
	};
	const openUserNameFullscreenModal = (userId: string) => {
		setLoadingId('');
		const path = `${import.meta.env.VITE_APP_URL}/#/people/${userId}`;
		window.open(path, '_blank');
	};
	const userNamesClick = (user: AccountUserViewModel) => async () => {
		setLoadingId(user.id);
		/**
		 * This is a work around to get the users internal contact id
		 * for the levitate main account as the user have a contact id already.
		 * @NOTE ONLY for levitate paid accounts will this be true.
		 */
		if (account.id === import.meta.env.VITE_MAIN_LEV_ACCOUNT_ID) {
			openUserNameFullscreenModal(user.contactId);
			return;
		}
		try {
			const opResult = await user.getProfileInternalContactId(user.id);
			openUserNameFullscreenModal(opResult.value);
		} catch (err) {
			setLoadingId('');
			logApiError('GetInternalId-Error', err);
			errorMessages.pushApiError(err);
		}
	};

	const resendWelcomeEmailMutation = useResendWelcomeEmailMutation();

	const resendEmailClick = (userId: string) => async () => {
		try {
			await resendWelcomeEmailMutation.mutateAsync({ userId });
			toast.success('Welcome email resent!');
		} catch (err) {
			errorMessages.pushApiError(err);
		}
	};
	const onChangeUserForDeactivateConfirmDialog = (user: AccountUserViewModel) => () => {
		setUserForDeactivateConfirmDialog(user);
		setShowDeactivateConfirmation(true);
	};
	const onDeactivateConfirmationRequestClose = (deactivate?: boolean) => {
		if (deactivate) {
			userForDeactivateConfirmDialog
				.updateUserStatus('deactivate')
				?.then(results => {
					toast.success(`${getDisplayName(results)} set to ${results.activationStatus.toLocaleLowerCase()}.`);
					invalidateAdminAccountUsersQuery(queryClient, account?.id ?? '');
				})
				.catch((err: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(err);
				});
		}
		setShowDeactivateConfirmation(false);
	};
	const userClick = (user: AccountUserViewModel) => () => {
		user.impersonate({ account, user: user.toJs() });
		setUserForInfoDialog(user);
	};
	const importHistoryModalClose = () => {
		setUserForImportHistoryDialog(null);
	};

	const onExportCommunicationHistory = (user: AccountUserViewModel) => () => {
		if (!exportCommunicationHistoryMutation.isPending) {
			exportCommunicationHistoryMutation.mutate({
				impersonationContext: {
					account,
					user: user?.toJs(),
				},
			});
		}
	};
	const importModalClose = () => {
		setShowImport(false);
	};

	const onRequestGrantSlotMachineSpinModalClose = () => {
		setSelectedUser(null);
		setShowingSlotMachineSpinModal(false);
	};
	const renderImportHistoryDialog = () => {
		if (!userForImportHistoryDialog) {
			return null;
		}
		return (
			<ImportHistory
				userSession={userSession}
				account={account}
				userVM={userForImportHistoryDialog}
				isOpen={!!userForImportHistoryDialog}
				onRequestClose={importHistoryModalClose}
			/>
		);
	};
	const renderEmailChangeModal = () => {
		return (
			<EmailChangeModal
				userSession={userSession}
				userVM={userForEmailChangeModal || userForAuthenticatorChangeEmailModal}
				account={account}
				isOpen={!!userForEmailChangeModal || !!userForAuthenticatorChangeEmailModal}
				onRequestClose={() => {
					setUserForEmailChangeModal(null);
					setUserForAuthenticatorChangeEmailModal(null);
					invalidateAdminAccountUsersQuery(queryClient, account?.id ?? '');
				}}
				type={userForAuthenticatorChangeEmailModal ? EmailChangeType.Authenticator : EmailChangeType.User}
			/>
		);
	};
	const renderSharedMailboxModal = () => {
		return (
			<SharedMailboxModal
				userSession={userSession}
				userVM={userForSharedMailboxModal}
				account={account}
				isOpen={!!userForSharedMailboxModal}
				onRequestClose={() => {
					setUserForSharedMailboxModal(null);
					invalidateAdminAccountUsersQuery(queryClient, account?.id ?? '');
				}}
			/>
		);
	};
	const totalPages = usersPage ? Math.ceil(usersPage.totalCount / PAGE_SIZE) : 0;

	return (
		<div className={`${css(styleSheet.container)} admin-accountDetail-userContainer`}>
			{isError && (
				<div className={css(styleSheet.errorContainer)}>
					<DangerMessage>
						{error instanceof Error ? error.message : 'Failed to load users'}
						<Button onClick={() => refetch()} styles={[styleSheet.retryButton]}>
							Retry
						</Button>
					</DangerMessage>
				</div>
			)}
			<UserFilters
				{...userFilters}
				roleOptions={ADMIN_USER_ROLE_OPTIONS}
				roleLabels={ADMIN_USER_ROLE_LABELS}
				userCount={usersPage?.totalCount ?? 0}
			/>
			<table
				className={`admin-accountDetail-employeeList ${css(styleSheet.employeeList)}`}
				style={isFetching && !isPending ? { opacity: 0.5 } : undefined}
			>
				<thead className={css(styleSheet.employeeListTableHead)}>
					<tr style={{ marginBottom: 40 }}>
						<th
							className={css(styleSheet.employeeListTableHeader, styleSheet.employeeListTableHeadShrink)}
							onClick={() => invalidateAdminAccountUsersQuery(queryClient, account?.id ?? '')}
						>
							<RefreshIcon
								fillColor={brandPrimary}
								hoverColor={brandPrimaryHover}
								className={css(styleSheet.refreshIcon)}
							/>
						</th>
						<th className={`${css(styleSheet.employeeListTableHeadName)} `}>
							<ConditionalAccess permission={AdminPermission.InviteUsers}>
								<button onClick={onInviteLinkClicked} className={css(styleSheet.inviteButton)}>
									<InviteMoreUsersIcon />
									<span style={{ marginLeft: 6 }}>Invite More</span>
								</button>
							</ConditionalAccess>
						</th>
						<th className={css(styleSheet.employeeListTableHeader)} />
						<th className={css(styleSheet.employeeListTableHeader)}>Connected</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Email Import</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Signature</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Add-In</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Mobile</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Tags</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Notes</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Facts</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Send</th>
						<th className={css(styleSheet.employeeListTableHeader)}>Test Email Sent</th>
						{account.features.socialMedia?.enabled && (
							<th className={css(styleSheet.employeeListTableHeader)}>Social Connected</th>
						)}
						<ConditionalAccess permission={AdminPermission.Deny}>
							<th className={css(styleSheet.additionalInfo)} />
						</ConditionalAccess>
					</tr>
					{account.emailProviderConfigurations.filter(epc => !isSupportedISP(epc.domain)).length > 0 ? (
						<tr>
							<td colSpan={5}>
								<DangerMessage styles={[styleSheet.unsupportedISP]}>
									One or more users below has a customer ISP email domain that we do not support. The customer must send
									through one of our domains like @myagent.email.
								</DangerMessage>
							</td>
						</tr>
					) : null}
				</thead>
				<tbody>
					{userViewModels.map(user => {
						return (
							<AccountUserRow
								account={account}
								grantTemporaryAccess={grantTemporaryAccess}
								importClick={importClick(user)}
								isDialogShowing={
									!!userForErrorsDialog ||
									!!userForImportHistoryDialog ||
									!!userForInfoDialog ||
									!!userForEmailChangeModal ||
									!!userForAuthenticatorChangeEmailModal
								}
								isLoadingUser={user.id && loadingId === user.id}
								key={user.id}
								onChangeEmail={onChangeEmail(user)}
								onAuthenticatorChangeEmail={onAuthenticatorChangeEmail(user)}
								onSharedMailbox={onSharedMailbox(user)}
								onCreateWelcomeEmailClick={onCreateWelcomeEmailClick(user)}
								onExportCommunicationHistory={onExportCommunicationHistory(user)}
								onGrantSlotMachineSpinClicked={onGrantSlotMachineSpinClicked}
								onImportHistory={onImportHistory(user)}
								onMakeMainContact={onMakeMainContact(user)}
								onUserDeleted={onUserDeleted}
								onUserErrorsClick={onUserErrorsClick(user)}
								onUserNameClick={isReseller ? undefined : userNamesClick(user)}
								resendEmailClick={resendEmailClick(user.id)}
								setUserForDeactivateConfirmDialog={onChangeUserForDeactivateConfirmDialog(user)}
								user={user}
								userClick={userClick(user)}
							/>
						);
					})}
				</tbody>
			</table>
			{totalPages > 1 && (
				<div className={css(styleSheet.paginationControls)}>
					<Button disabled={page === 0 || isFetching} onClick={() => setPage(p => p - 1)} size='sm' variant='secondary'>
						← Prev
					</Button>
					<span className={css(styleSheet.pageInfo)}>
						Page {page + 1} of {totalPages}
					</span>
					<Button
						disabled={page >= totalPages - 1 || isFetching}
						onClick={() => setPage(p => p + 1)}
						size='sm'
						variant='secondary'
					>
						Next →
					</Button>
				</div>
			)}
			{showDeactivateConfirmation && (
				<DeactivateUserModal
					account={account}
					onClose={onDeactivateConfirmationRequestClose}
					userId={userForDeactivateConfirmDialog.id}
				/>
			)}
			{isPending && (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
				</div>
			)}
			{renderImportHistoryDialog()}
			<ContactImport
				impersonationContext={selectedUser && account ? { account, user: selectedUser.toJs() } : undefined}
				isOpen={showImport}
				onRequestClose={importModalClose}
				showDeleteRenewalKeyFactsOption={account?.preferences?.deleteRenewalKeyFacts}
				user={selectedUser}
			/>
			{userForInfoDialog ? (
				<Modal isOpen={true} onRequestClose={() => setUserForInfoDialog(null)} useDefaultHeader={true}>
					<UserDialogContent user={userForInfoDialog} account={account} />
				</Modal>
			) : null}
			<UserErrorsDialog onClose={() => setUserForErrorsDialog(null)} user={userForErrorsDialog} />
			{renderEmailChangeModal()}
			{userForSharedMailboxModal !== null ? renderSharedMailboxModal() : null}
			<GrantSlotMachineSpinModal
				modalProps={{
					isOpen: showingSlotMachineSpinModal,
					onRequestClose: onRequestGrantSlotMachineSpinModalClose,
				}}
				user={selectedUser}
			/>
			<ExportConfirmationModalV2
				mutationResult={exportCommunicationHistoryMutation}
				modalProps={{
					isOpen: !exportCommunicationHistoryMutation.isIdle,
					onRequestClose: onExportCommunicationHistoryClosed,
				}}
				subTitle={
					<>
						<p>Export of recent history will be delivered to your mailbox</p>
					</>
				}
			/>
		</div>
	);
}

export const Users = _Users;
